<script setup lang="ts">
import InfluencerCard from '~/components/influencer/card.vue'
import SeoListDescription from '~/components/seo/description.vue'
import SeoFooter from '~/components/seo/footer/index.vue'
import SeoListNudge from '~/components/seo/nudge.vue'
import SeoListSubtitle from '~/components/seo/subtitle.vue'
import SeoListTitle from '~/components/seo/title.vue'
import Footer from '~/components/shared/footer/index.vue'
import PaginationNavigation from '~/components/shared/paginationNavigation/index.vue'
import CampaignFooter from '~/components/ui/CampaignFooter/index.vue'

import { useGetCurrentFullUrl } from '~/composables/useGetCurrentFullUrl'
import { useInfluencerListTagSeo } from '~/composables/useInfluencerListTagSeo'
import { useIntercomMove } from '~/composables/useIntercomMove'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSegmentTrackCartAdditions } from '~/composables/useSegmentTrackCartAdditions'
import { useSeoListGeneration } from '~/composables/useSeoListGeneration'

import { useInfluencersStore } from '~/stores/influencers'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'

import { configToQs } from '~/helpers/configToQs'
import {
  checkTagArgs,
  checkTagOrders,
  checkTypesPairing,
  getNeedToFetchInfluencerList,
  getPageStartIndex,
  isTagParent,
  tagTypesAreAllowed,
} from '~/helpers/pages/influencerListTagSeo'

import type { AvailablePageTypes } from '~/components/ui/CampaignFooter/index.vue'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { SeoListsAsyncData } from '~/types/influencerListTagSeo'
import type { SeoListing } from '~/types/seoList'

interface ComponentConfigTemplate<
  T extends typeof SeoListNudge | typeof InfluencerCard,
> {
  component: T
  key: string
}

interface ComponentConfigNudgeCard
  extends ComponentConfigTemplate<typeof SeoListNudge> {
  props: {
    urlData: SeoListsAsyncData['urlData']
  }
}

interface ComponentConfigInfluencerCard
  extends ComponentConfigTemplate<typeof InfluencerCard> {
  props: {
    influencer: Influencer | StatsV3Influencer
    seoPageUrlData: SeoListsAsyncData['urlData']
  }
}

definePageMeta({
  middleware: ['tag-fetch'],
})

const { t } = useI18n()
const route = useRoute()
const localePath = useLocalePath()
const { coreFetch } = useProvideCoreFetch()
useIntercomMove()
useSegmentTrackCartAdditions()
const { getArg, getParamTagType } = useInfluencerListTagSeo()
const { getCurrentFullUrl } = useGetCurrentFullUrl()

const { FETCH_SET } = useInfluencersStore()
const { GET_BY_IDS_SORTED: GET_INFLUENCERS_SORTED_BY_IDS } =
  useInfluencersStore()
const { IS_BAND: USER_IS_BAND } = storeToRefs(useUserStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const slug = (route.params.slug as string[]).filter((entry) => entry.length > 0)
const errorArgs = {
  message: 'Could not find desired tags',
}
const limit = 30
const urlData = ref([] as SeoListsAsyncData['urlData'])
const currentPage = ref(0)

// Fetch target Tag / TagParent data from names
const tagArgs = slug.map((param) => getArg(param))
// Check if missing Tag / TagParent data, abort if missing
if (!checkTagArgs(tagArgs)) throw new Error(errorArgs.message)

// Retrieve types from Tag / TagParent array
const types = tagArgs.map((e) => getParamTagType(e))
// Make sure the present types are allowed
// And make sure the pairings are correct: eg. subgenre/subgenre is not valid but subgenre/country is
if (!tagTypesAreAllowed(types) || !checkTypesPairing(tagArgs))
  throw new Error(errorArgs.message)

// Check if the order of tags is valid in the urls, if not, swap both parties and redirect
const redirectData = checkTagOrders(types, tagArgs)
if (redirectData) {
  await navigateTo(localePath(`/influencer/list/tags/${redirectData}`), {
    replace: true,
  })
}

urlData.value = tagArgs.map((e, index) => ({
  tagTypeName: types[index].name,
  tagId: e.id,
  isParent: isTagParent(e),
}))

const { generateTopTitleFromUrlData } = useSeoListGeneration()
const title = generateTopTitleFromUrlData(urlData.value)
const description = t('pages.seo.meta.description', {
  title,
})

useHead({
  title,
  meta: [
    {
      name: 'description',
      key: 'description',
      content: description,
    },
    {
      property: 'og:title',
      key: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      key: 'og:description',
      content: description,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: getCurrentFullUrl(),
    },
  ],
})

const { data: influencers } = await useAsyncData<number[]>(
  `${tagArgs.map((e) => e.id).toString()}::${types.map((e) => e.id).toString()}`,
  async () => {
    const influencers = await getNeedToFetchInfluencerList(
      coreFetch,
      tagArgs,
      types,
    )

    await FETCH_SET(influencers)
    return influencers
  },
  {
    default: () => [],
  },
)

const { data: footerData } = useAsyncData(
  'footerData',
  () =>
    coreFetch.$get<SeoListing[]>(
      `/seo/lists/?${configToQs({
        tag_ids: tagArgs
          .filter((e) => !isTagParent(e))
          .map((e) => e.id)
          .toString(),
        parent_ids: tagArgs
          .filter((e) => isTagParent(e))
          .map((e) => e.id)
          .toString(),
      })}`,
    ),
  { default: () => [] },
)

currentPage.value = getPageStartIndex(
  route.query,
  influencers.value.length,
  limit,
).currentPage

const campaignFooterPage = computed<AvailablePageTypes>(() =>
  USER_IS_BAND.value ? 'homepage' : 'seoList',
)
const ALL_INFLUENCERS = computed(() =>
  GET_INFLUENCERS_SORTED_BY_IDS(influencers.value),
)
const DISPLAYED_INFLUENCERS = computed(() =>
  ALL_INFLUENCERS.value.slice(offset.value, offset.value + limit),
)
const paginationCap = computed<1 | 3>(() => (SCREEN_WIDTH.value < 600 ? 1 : 3))
const maxPageCount = computed<number>(() =>
  Math.ceil(ALL_INFLUENCERS.value.length / limit),
)
const offset = computed<number>(() => currentPage.value * limit)
const displayWithInsertedNudges = computed<
  (ComponentConfigNudgeCard | ComponentConfigInfluencerCard)[]
>(() => {
  const out: (ComponentConfigNudgeCard | ComponentConfigInfluencerCard)[] = []
  const spacing = 20 as const
  let counter: number = spacing

  for (const index in DISPLAYED_INFLUENCERS.value) {
    const influencer = DISPLAYED_INFLUENCERS.value[index]

    if (!USER_IS_BAND.value) {
      if (counter >= spacing) {
        out.push({
          component: SeoListNudge,
          key: `${index}_nudge`,
          props: {
            urlData: urlData.value,
          },
        })
        counter = 0
      } else {
        counter++
      }
    }

    out.push({
      component: InfluencerCard,
      key: `${index}_influencer`,
      props: {
        influencer,
        seoPageUrlData: urlData.value,
      },
    })
  }
  return out
})
</script>

<template>
  <div id="ignoreFontUpscale" class="mainParentContainer ignoreFontUpscale">
    <!-- Listing Outer Wrap-->
    <div>
      <div
        v-if="urlData.length"
        class="tw-mb-2xl tw-mt-4xl tw-grid tw-grid-cols-1 tw-gap-sm"
      >
        <SeoListTitle :url-data="urlData" />
        <SeoListSubtitle
          :url-data="urlData"
          :influencer-count="influencers.length"
        />
        <SeoListDescription
          :url-data="urlData"
          :influencers="DISPLAYED_INFLUENCERS"
        />
      </div>
      <!-- Listing -->
      <transition name="fade" mode="out-in">
        <div :key="currentPage" class="gridWrap" :class="{ USER_IS_BAND }">
          <component
            v-bind="props"
            :is="component"
            v-for="{ key, props, component } in displayWithInsertedNudges"
            :key="key"
            clicked-from="seo_page"
          />
        </div>
      </transition>
      <!-- Paginator -->
      <div
        class="tw-mx-auto tw-mb-8 tw-mt-6 tw-flex tw-w-auto tw-items-center tw-justify-center"
      >
        <PaginationNavigation
          ref="pagination"
          v-model="currentPage"
          :pagination-cap="paginationCap"
          :max-page-count="maxPageCount"
        />
      </div>
    </div>
    <SeoFooter :lists="footerData" :url-data="urlData" />
    <Footer class="tw-w-force-full-grid-system" />
    <CampaignFooter :page="campaignFooterPage" />
  </div>
</template>

<style scoped lang="scss">
.gridWrap {
  @apply tw-grid tw-grid-cols-1 tw-gap-4;

  grid-template-rows: 256px;

  &.USER_IS_BAND {
    grid-template-rows: 304px;
  }

  @screen 600 {
    @apply tw-grid-cols-2 tw-gap-x-4 tw-gap-y-6;
  }

  @screen md {
    @apply tw-gap-6;
  }

  @screen lg {
    @apply tw-grid-cols-3;
  }
}
</style>
